import { useId, useRef, useState } from 'react'
import { useDismiss } from '@/hooks/useDismiss'
import { cn } from '@/utils/cn'
import type { ReaderLeading, ReaderSettingsValue } from '@/hooks/useReaderSettings'
import { filledFill, focusRing, iconFill, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'
import { useSheetAnchor } from '@/hooks/useSheetAnchor'
import { useIsPhone } from '@/hooks/useIsPhone'
import { SheetHandle, SheetWrapper, sheetDocked, sheetSafeArea } from '@/components/ui/Sheet'

interface ReaderSettingsProps {
  settings: ReaderSettingsValue
  setSize: (size: number) => void
  setLeading: (leading: ReaderLeading) => void
  className?: string
}

const LEADING_OPTIONS: { value: ReaderLeading; label: string }[] = [
  { value: 'snug', label: 'Snug' },
  { value: 'normal', label: 'Normal' },
  { value: 'loose', label: 'Loose' },
]

const iconButton = iconFill + ' disabled:opacity-30'

/**
 * "Aa" popover for the reading passage: five size steps and three leading options.
 * Positioned under its trigger; closes on outside click or Escape and hands focus back.
 */
export function ReaderSettings({ settings, setSize, setLeading, className }: ReaderSettingsProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const isPhone = useIsPhone()
  const leadingLabelId = useId()

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  useDismiss(wrapperRef, close, open, { alsoInside: sheetRef })
  useSheetAnchor(open, triggerRef, sheetRef)

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-label="Reading settings"
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(iconButton, 'font-serif text-lg', open && filledFill)}
      >
        Aa
      </button>

      {open && (
        <SheetWrapper isPhone={isPhone} onScrimClick={close}>
        <div
          ref={sheetRef}
          role="dialog"
          aria-label="Reading settings"
          data-testid={isPhone ? 'bottom-sheet' : undefined}
          className={cn(
            'animate-fade-in',
            isPhone
              ? cn(sheetDocked, sheetSafeArea, 'px-5 pb-4')
              : 'absolute right-0 top-full z-40 mt-2 w-64 rounded-lg border border-border-divider bg-card p-4 shadow-lg',
          )}
        >
          <SheetHandle />
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs uppercase tracking-widest text-muted">Text size</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setSize(settings.size - 1)}
                disabled={settings.size <= 1}
                aria-label="Smaller text"
                className={cn(iconButton, 'font-serif text-sm')}
              >
                A
              </button>
              <span className="w-10 text-center font-mono text-xs text-muted" aria-live="polite">
                <span className="text-body">{settings.size}</span>
                <span aria-hidden="true"> / 5</span>
                <span className="sr-only"> of 5</span>
              </span>
              <button
                type="button"
                onClick={() => setSize(settings.size + 1)}
                disabled={settings.size >= 5}
                aria-label="Larger text"
                className={cn(iconButton, 'font-serif text-xl')}
              >
                A
              </button>
            </div>
          </div>

          <div className="mt-4 border-t border-line-soft pt-4">
            <span id={leadingLabelId} className="font-mono text-xs uppercase tracking-widest text-muted">
              Line spacing
            </span>
            <div role="radiogroup" aria-labelledby={leadingLabelId} className="mt-2 grid grid-cols-3 gap-1">
              {LEADING_OPTIONS.map(({ value, label }) => {
                const checked = settings.leading === value
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    onClick={() => setLeading(value)}
                    className={cn(
                      'min-h-11 rounded-md text-sm font-normal transition-colors duration-200',
                      focusRing,
                      checked ? segmentedSelected : segmentedRest,
                    )}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        </SheetWrapper>
      )}
    </div>
  )
}
